import { useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { useApp } from "../lib/AppContext";
import { PageHead, Panel, Btn } from "../components/common";
import { Field, useSettingsForm } from "./Risk";

function Mfa() {
  const { user, setUser } = useApp();
  const [qr, setQr] = useState(null);
  const [code, setCode] = useState("");
  const setup = async () => setQr((await api.post("/auth/mfa/setup")).data);
  const enable = async () => { try { await api.post("/auth/mfa/enable", { code }); toast.success("MFA enabled"); setUser({ ...user, mfa_enabled: true }); setQr(null); setCode(""); } catch (e) { toast.error(errMsg(e)); } };
  const disable = async () => { try { await api.post("/auth/mfa/disable", { code }); toast.success("MFA disabled; live authorization revoked"); setUser({ ...user, mfa_enabled: false }); setCode(""); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <Panel title="Multi-factor authentication" testid="mfa-panel">
      <div className="text-xs">Status: <span className={user?.mfa_enabled ? "text-emerald-600" : "text-amber-600"}>{user?.mfa_enabled ? "ENABLED" : "DISABLED"}</span></div>
      {!user?.mfa_enabled && !qr && <Btn className="mt-2" onClick={setup} data-testid="mfa-setup-btn">Set up authenticator</Btn>}
      {qr && <div className="mt-2"><img src={qr.qr} alt="MFA QR" className="h-40 w-40 bg-white p-1" /><div className="mono mt-1 break-all text-[10px] text-slate-500">{qr.secret}</div></div>}
      {(qr || user?.mfa_enabled) && <div className="mt-2 flex gap-2"><input data-testid="mfa-code-input" value={code} onChange={(e) => setCode(e.target.value)} placeholder="6-digit code" className="mono w-28 rounded-sm border border-[#E2E8F0] bg-[#F8FAFC] px-2 py-1.5 text-xs" />
        {qr ? <Btn onClick={enable} data-testid="mfa-enable-btn">Enable</Btn> : <Btn variant="danger" onClick={disable} data-testid="mfa-disable-btn">Disable</Btn>}</div>}
    </Panel>
  );
}

function Password() {
  const [a, setA] = useState(""); const [b, setB] = useState("");
  const go = async () => { try { await api.post("/auth/change-password", { current_password: a, new_password: b }); toast.success("Password changed"); setA(""); setB(""); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <Panel title="Change password">
      <input type="password" data-testid="pw-current" value={a} onChange={(e) => setA(e.target.value)} placeholder="current" className="mb-2 w-full rounded-sm border border-[#E2E8F0] bg-[#F8FAFC] px-2 py-1.5 text-xs" />
      <input type="password" data-testid="pw-new" value={b} onChange={(e) => setB(e.target.value)} placeholder="new (min 8)" className="mb-2 w-full rounded-sm border border-[#E2E8F0] bg-[#F8FAFC] px-2 py-1.5 text-xs" />
      <Btn onClick={go} data-testid="pw-save">Update</Btn>
    </Panel>
  );
}

export default function Settings() {
  const f = useSettingsForm(["starting_capital", "strategy_timeframe", "cycle_seconds", "time_exit_bars", "order_expiry_cycles", "trail_atr_mult", "ai_provider", "owner_email", "language", "markets", "notify_channels"]);
  const tog = (grp, k) => f.setForm({ ...f.form, [grp]: { ...f.form[grp], [k]: !f.form[grp]?.[k] } });
  return (
    <div data-testid="settings-page">
      <PageHead title="Settings" sub="All trading parameters are owner-configurable. Risk policy lives in Capital Shield; target rules in Daily Profit Target." right={<Btn onClick={f.save} data-testid="settings-save-btn">Save settings</Btn>} />
      <div className="grid gap-3 lg:grid-cols-3">
        <Panel title="Engine">
          <Field label="Starting capital (MYR, locked after first trade)" k="starting_capital" {...f} set={f.setForm} />
          <Field label="Strategy timeframe" k="strategy_timeframe" options={["5m", "15m", "30m", "1h"]} {...f} set={f.setForm} />
          <Field label="Cycle seconds" k="cycle_seconds" {...f} set={f.setForm} />
          <Field label="Time exit (bars)" k="time_exit_bars" {...f} set={f.setForm} />
          <Field label="Order expiry (cycles)" k="order_expiry_cycles" {...f} set={f.setForm} />
          <Field label="Trailing stop ATR mult" k="trail_atr_mult" {...f} set={f.setForm} />
          <Field label="AI explanation model" k="ai_provider" options={["anthropic", "openai"]} {...f} set={f.setForm} />
        </Panel>
        <Panel title="Markets & notifications">
          {["BURSA", "US", "HK", "SG"].map((m) => <label key={m} className="flex justify-between py-1 text-xs"><span>{m}</span><input type="checkbox" data-testid={`market-${m}`} checked={!!f.form.markets?.[m]} onChange={() => tog("markets", m)} className="accent-blue-600" /></label>)}
          <div className="my-2 border-t border-[#E2E8F0]" />
          {["email", "smtp", "whatsapp", "inapp"].map((c) => <label key={c} className="flex justify-between py-1 text-xs"><span>{c}</span><input type="checkbox" data-testid={`channel-${c}`} checked={!!f.form.notify_channels?.[c]} onChange={() => tog("notify_channels", c)} className="accent-blue-600" /></label>)}
          <Field label="Owner email" k="owner_email" type="email" {...f} set={f.setForm} />
        </Panel>
        <div className="space-y-3"><Mfa /><Password /><Brand /></div>
      </div>
    </div>
  );
}

const BRAND = [["light", "Light (for white backgrounds)", "bg-white"], ["dark", "Dark (for dark backgrounds)", "bg-[#0B1220]"], ["icon", "Icon only (app / favicon)", "bg-[#F8FAFC]"]];

function Brand() {
  return (
    <Panel title="Brand assets (Figma-ready SVG)" testid="brand-assets">
      <div className="space-y-2">{BRAND.map(([k, label, bg]) => (
        <div key={k} className="flex items-center gap-3 rounded-sm border border-[#E2E8F0] p-2">
          <div className={`grid h-12 w-28 shrink-0 place-items-center rounded-sm ${bg}`}><img src={`/brand/dmi-logo-${k}.svg`} alt={`DMI logo ${k}`} className="max-h-10 max-w-[104px]" /></div>
          <div className="min-w-0 flex-1 text-xs text-slate-700">{label}</div>
          <a href={`/brand/dmi-logo-${k}.svg`} download data-testid={`brand-download-${k}`} className="mono rounded-sm border border-[#E2E8F0] px-2 py-1 text-[10px] text-blue-600 hover:bg-[#F1F5F9]">SVG</a>
        </div>))}
      </div>
      <p className="mt-2 text-[10px] text-slate-500">Drag an SVG into Figma to edit as vectors. Wordmark uses the Chivo font (free on Google Fonts).</p>
    </Panel>
  );
}
